import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { HttpRequestService } from "app/auth/service/http-request.service";
import { pick } from "lodash";
import { Router } from "@angular/router";
import { CoreConfigService } from "@core/services/config.service";
import Swal from "sweetalert2";
import { catchError, map, timeout } from "rxjs/operators";
import { environment } from "environments/environment";
import { LOCAL_STORAGE_KEYS } from "app/main/shared/enums/LocalStorage.enum";
import { User } from "app/auth/models";
import { ToastrService } from "ngx-toastr";
import { SAML2ConfigInterface } from "app/main/shared/interfaces/saml2-response.interface";
import { HttpClient } from "@angular/common/http";
import { SessionService } from "app/main/services/session.service";
import { ROLE } from "app/main/shared/enums/Roles.enum";
import { ReportFiltersService } from 'app/main/pages/reports/report-filters.service';

type TwoFA = {
  active: boolean;
  telephone: string;
  email: string;
  otpTtl: number;
};

export type LoggedResponse = {
  user: User;
  token: string;
  twoFA: TwoFA;
  enableSenderManagement?: boolean;
};
export type UserLoginCredentials = {
  username: string;
  password: string;
};
@Injectable({ providedIn: "root" })
export class AuthenticationService {
  public loading = false;
  private pendingLogout: { token: string; request: Promise<boolean> } | null = null;
  public currentUser: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public url = `${environment.apiUrl}/public/auth`;
  private saml2BaseUrl = `${environment.apiUrl}/public/saml2`;
  public showSaml2LoginSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public companySaml2IdSubject: BehaviorSubject<number> =
    new BehaviorSubject<number>(null);

  constructor(
    private httpService: HttpRequestService,
    private router: Router,
    private http: HttpClient,
    private toastrService: ToastrService,
    private readonly sessionService: SessionService,
    public coreConfigService: CoreConfigService,
    private readonly reportFilterService: ReportFiltersService
  ) {}

  login = (credentials: UserLoginCredentials): Observable<any> =>
    this.http.post(`${this.url}/login`, credentials).pipe(
      map((res: LoggedResponse) => {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.CONFIG);
        console.log("Two factor", res.twoFA);
        console.log("Sender management enabled", res.enableSenderManagement);
        if (!res.twoFA.active)
          this.setUserLoggedCredentials(res, res.token);
        return res;
      }),
      catchError((error: any) => {
        console.error("login catch error", error);
        const cause = error?.details?.message;
        let msg = "Hubo un error en la autenticación";

        if (cause === "user_is_logged") {
          msg = "Usuario ya tiene una sesion iniciada";
        } else if (cause === "auth_invalidated") {
          msg = "Usuario ó contraseña incorrectos";
        } else if (
          cause === "inauthorized_owner" ||
          cause === "inauthorized_company" ||
          cause === "inauthorized_user"
        ) {
          msg = "Acceso denegado";
        }
        throw msg;
      })
    );

  setUserLoggedCredentials = (res: LoggedResponse, token: string) => {
    // Store enableSenderManagement in localStorage
    const authData = {
      ...res.user,
      enableSenderManagement: res.enableSenderManagement ?? false
    };
    localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH, JSON.stringify(authData));
    this.setLoggedUserData(res.user, true);
  };

  refreshToken = (token: string): Observable<any> => {
    return this.http.post(`${this.url}/refresh-token`, { token }).pipe(
      map((res: LoggedResponse) => {
        this.setUserLoggedCredentials(res, res.token);
        return res;
      }),
      catchError((error: any) => {
        const cause = error?.details?.message;
        let msg = "Hubo un error en la autenticación";

        if (cause === "user_is_logged") {
          msg = "Usuario ya tiene una sesion iniciada";
        } else if (cause === "auth_invalidated") {
          msg = "Usuario ó contraseña incorrectos";
        } else if (
          cause === "inauthorized_owner" ||
          cause === "inauthorized_company" ||
          cause === "inauthorized_user"
        ) {
          msg = "Acceso denegado";
        }
        throw msg;
      })
    );
  };

  loginSaml2() {
    this.http
      .get(`${this.saml2BaseUrl}/login/${this.companySaml2IdSubject.value}`)
      .subscribe((res: { url: string }) => {
        const url = res.url;

        if (!res) {
          this.toastrService.error("Error al obtener configuración de SAML2");
          return;
        }
        window.location.href = url;
      });
  }

  logout(): Promise<boolean> {
    const token = this.token;
    if (!token) return Promise.resolve(this.clearSession(token));
    if (this.pendingLogout?.token === token) return this.pendingLogout.request;

    const request = this.http.post(`${this.url}/logout`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).pipe(timeout(10000)).toPromise().then(
      () => this.clearSession(token),
      () => {
        this.toastrService.error('No se pudo cerrar la sesión. Inténtalo nuevamente.');
        return false;
      },
    );
    this.pendingLogout = { token, request };
    void request.then(() => {
      if (this.pendingLogout?.request === request) this.pendingLogout = null;
    });
    return request;
  }

  // An already rejected session needs no further logout request. Compare the
  // token so a late response from an earlier session cannot clear a new login.
  clearSession(expectedToken: string | null): boolean {
    if (this.token !== expectedToken) return false;
    this.currentUser.next(null);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.CONFIG);
    this.reportFilterService.clearAllFilters();
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.SHARED_TOKEN)) {
      this.sessionService.restoreSession();
    } else {
      this.router.navigate(['/autenticacion/login']);
    }
    return true;
  }

  setLoggedUserData = (user, login = false): void => {
    const authData = {
      ...pick(user, [
        "rol_id",
        "company_id",
        "id",
        "username",
        "permission",
        "first_name",
        "last_name",
        "blackout",
        "role.name",
        "logo",
        "loading_logo",
        "theme_config_id",
        "owner_id",
        "require_authorization",
        "is_authorizing",
        "can_use_web",
        "email",
        "emgSend",
        "company",
        "provisionings",
        "limits",
        "consumption",
        "enableSenderManagement",
      ]),
      // enableSenderManagement: this.enableSenderManagement,
      permission: user.permission,
      role: user.role.name,
      is_owner: Boolean(user.company?.is_owner),
      logo: user.company?.logo,
      loading_logo: user.company?.loading_logo,
      theme_config_id: user.company?.theme_config_id,
    };
    console.log("USER LOGGED", user);
    login || !localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH)
      ? localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH, JSON.stringify(authData))
      : "";
    this.currentUser.next(authData);
  };

  forgotPassword = (conf): Observable<any> =>
    this.httpService.ForgotPassword().post(conf);

  restorePassword = (conf): Observable<any> =>
    this.httpService.RestorePassword().post(conf);

  verifyPasswordReset = (token: string, code: string): Observable<{ verified: boolean; token: string; ttlSeconds: number }> =>
    this.http.post<{ verified: boolean; token: string; ttlSeconds: number }>(`${this.url}/validate-otp`, { token, code });

  loding2FA = (token: string, code: string): Observable<any> =>
    this.http
      .post(`${this.url}/login-2fa`, {
        token,
        code,
      });

  sendOtpAuthOtp = (otpParams) =>
    this.http.post<{
      token: string;
      duration: number;
      ttlSeconds?: number;
      resendAfterSeconds?: number;
    }>(`${this.url}/send-otp`, otpParams);

  setInactivityTime = () => {
    if (!this.isLogged) return;
    let time;
    const minutes = 30;
    const resetTimer = () => {
      clearTimeout(time);
      time = setTimeout(async () => {
        const closed = await this.logout();
        if (!closed) {
          resetTimer();
          return;
        }
        Swal.fire("Se ha cerrado la sesión por inactividad", "", "warning");
        window.document.onmousemove = null;
        window.document.onkeypress = null;
        window.onload = null;
      }, 60000 * minutes);
    };
    window.onload = resetTimer;
    window.document.onmousemove = resetTimer;
    window.document.onkeypress = resetTimer;
  };

  // * ------------------------- saml2 ------------------------------
  getSaml2Config(host: string): Observable<SAML2ConfigInterface> {
    return this.http.get<SAML2ConfigInterface>(
      `${this.saml2BaseUrl}/find-saml2-config/${host}`
    );
  }

  retoreSwichSession = (userId: string) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.TOKEN,
      localStorage.getItem(LOCAL_STORAGE_KEYS.SHARED_TOKEN)
    );
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SHARED_TOKEN);
  };

  get authData() {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH));
  }

  // * ------------------------- I D S ------------------------------
  get userId() {
    return this.authData?.id;
  }

  get companyId() {
    return this.authData?.company_id;
  }

  get companyOwnerId() {
    return this.user?.owner_id || this.authData?.owner_id;
  }

  get roleId() {
    return this.authData?.rol_id;
  }

  get permission() {
    return this.user?.permission?.enabled ? this.user?.permission : null;
  }

  get companyTz() {
    return this.user?.company?.tz;
  }

  // * ------------------------- I D S ------------------------------

  get token() {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  }

  get user() {
    return this.currentUser.value || null;
  }

  get blackout() {
    return this.user?.blackout;
  }

  get role(): ROLE {
    return (
      this.user?.role?.name || this.user?.role || this.authData?.role?.name
    );
    // return this.user?.role;
  }

  get isSuperAdmin() {
    return this.role === ROLE.SUPER_ADMIN;
  }

  get isAdmin() {
    return this.role === ROLE.ADMIN;
  }

  get isSubAdmin() {
    return this.role === ROLE.SUBADMIN;
  }

  get isSender() {
    return this.role === ROLE.SENDER;
  }

  get isLogged() {
    return this.token ? true : false;
  }

  get isOwner() {
    return this.user.is_owner;
  }

  get canSendByWeb() {
    return this.user.can_use_web;
  }

  // Use localStorage for enableSenderManagement
  get enableSenderManagement(): boolean {
    const authData = this.authData;
    return authData?.enableSenderManagement ?? false;
  }
  set enableSenderManagement(value: boolean) {
    const authData = this.authData || {};
    authData.enableSenderManagement = value;
    localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH, JSON.stringify(authData));
  }
}
